import React from "react";
import useCustomQueryClient from "data/modules/api/hooks/useQueryClient";
import { is_empty } from "features/Common/utils/common.utils";
import { EVENT_SOURCES, EVENT_TYPES } from "../SSE.constants";
import {
  getRAUrlParams,
  getRAUrlPathName,
} from "features/Common/utils/url.utils";
import { useInboxStore } from "features/OmniChannel/modules/Inbox/store/useInboxStore";
import {
  OMNICHANNEL_ROUTE_IDS,
  OMNICHANNEL_URL_KEYS,
} from "features/OmniChannel/OmniChannel.urlPaths";
import { app_route_ids, app_route_keys } from "constants/urlPaths";
import { logError } from "utils/error";
import { ENTITY_TYPE } from "constants/crmConstants";
import { getTimelineLogsQueryKeys } from "features/Crm/modules/CustomerDetails/apis/queryKeys";
import { INITIAL_PAGE } from "features/Crm/Crm.constants";
import {
  getContactsListQueryKeys,
  getInboxNotificationsCountQueryKeys,
} from "features/OmniChannel/modules/Inbox/apis/inboxQueryKeys";
import { useContactFiltersStore } from "features/OmniChannel/modules/Inbox/modules/ContactsList/ContactFilters/store/useContactFiltersStore";
import { getContactsListFiltersParams } from "features/OmniChannel/modules/Inbox/modules/ContactsList/ContactsList.utils";
import { notification_variant_map, useNotifications } from "utils/hooks";
import {
  CustomNoification,
  CustomNotificationIcon,
} from "../modules/CustomNotification";
import { useMarkConversationsAsRead } from "features/OmniChannel/modules/Inbox/modules/ContactTimeline/hooks/useMarkConversationsAsRead";
import {
  AI_PAGE_BUILDER_ROUTE_IDS,
  AI_PAGE_BUILDER_ROUTE_KEYS,
} from "features/SalesPages/modules/AiPageBuilder/constants/AiSalePageBuilder.constants";

export const useMessageEventHandlers = () => {
  const { markConversationAsRead } = useMarkConversationsAsRead({});
  const queryClient = useCustomQueryClient();
  const { pushNotification } = useNotifications();
  const getSelectedContact = useInboxStore(
    (state) => state.actions.getSelectedContact
  );
  const getAppliedFilters = useContactFiltersStore(
    (state) => state.actions.getAppliedFilters
  );

  const queryInvalidate = (queryKey) => {
    queryClient.invalidateQueries({ queryKey });
  };

  const notificationHandler = (eventData) => {
    return pushNotification(<CustomNoification {...eventData} />, {
      id: eventData?.id,
      variant: notification_variant_map.outlined,
      timeout: 10 * 1000, // 10 seconds
      otherProps: {
        icon: <CustomNotificationIcon iconUrl={eventData.icon_url} />,
        toastClassName:
          "!tw-flex !tw-items-start !tw-gap-2 !tw-border !tw-border-solid !tw-border-[#493ab1] !tw-bg-[#f6f5fd] !tw-py-2 !tw-pr-0 !tw-pl-4 !tw-rounded-lg",
      },
    });
  };

  // handling logic for event_type of REFRESH_OR_NOTIFICATION
  const refreshOrNotificationHandler = (eventData) => {
    const pathname = getRAUrlPathName();
    const urlParams = getRAUrlParams();

    if (Object.values(EVENT_SOURCES).includes(eventData?.event_source)) {
      const entityId = urlParams?.get("id");
      const finalPathname = `/${pathname}`;

      // refresh inbox notification count
      queryInvalidate(getInboxNotificationsCountQueryKeys.notificationsCount);

      switch (finalPathname) {
        case OMNICHANNEL_ROUTE_IDS[OMNICHANNEL_URL_KEYS.inbox]: {
          const selectedContactId =
            getSelectedContact()?.leadId || getSelectedContact()?.id;
          const { unreadApplied, openApplied, selectedOrgIds } =
            getAppliedFilters();
          const params = getContactsListFiltersParams(
            unreadApplied,
            openApplied,
            selectedOrgIds
          );

          if (eventData?.lead_id == selectedContactId) {
            // refresh timeline
            queryInvalidate(
              getTimelineLogsQueryKeys(INITIAL_PAGE, selectedContactId)
                .timelineLogs
            );
            markConversationAsRead(selectedContactId, true);
          }

          // refresh inbox contacts
          queryInvalidate(getContactsListQueryKeys(params).contactsList);

          break;
        }

        case AI_PAGE_BUILDER_ROUTE_IDS[AI_PAGE_BUILDER_ROUTE_KEYS.editor]: {
          const selectedConversationUid = urlParams?.get("conversationUid");
          if (eventData?.ai_conversation_uid != selectedConversationUid) {
            notificationHandler(eventData);
            return;
          }
          window.location.reload();
          break;
        }

        case app_route_ids[app_route_keys.customer_details]: {
          const entityType = urlParams?.get("entity_type");
          const isLead = entityType == ENTITY_TYPE.lead.value;
          const matchedEntity = isLead
            ? eventData?.lead_id
            : eventData?.customer_username;

          if (entityId == matchedEntity) {
            // refresh timeline
            queryInvalidate(
              getTimelineLogsQueryKeys(INITIAL_PAGE, entityId).timelineLogs
            );
          } else notificationHandler(eventData);
          break;
        }

        default: {
          notificationHandler(eventData);
          break;
        }
      }
    }
  };

  const handleMessage = (event) => {
    try {
      const eventData = JSON.parse(event.data);
      if (is_empty(eventData)) return;

      switch (Number(eventData?.event_type)) {
        case EVENT_TYPES.REFRESH_OR_NOTIFICATION:
          refreshOrNotificationHandler(eventData);
          break;
        case EVENT_TYPES.REFRESH:
          // Refresh handler to be introuced when the case arrives in future
          break;
        case EVENT_TYPES.NOTIFICATION:
          notificationHandler(eventData);
          break;
        default:
          break;
      }
    } catch (error) {
      logError({
        error,
        when: "handleMessage",
        occuredAt: "src/features/SSE/utils/messageEventHandler.js",
      });
    }
  };

  return {
    handleMessage,
  };
};
